import { z } from 'zod';
import {
  OptionsSchema as BaseOptionsSchema,
  AuiTokenSchema,
  TriggerUnionSchema,
  TriggerOptionSchema,
  TextPropsSchema,
} from '../../WidgetVariant';

const TopicSchema = z.object({
  auiIcon: z.string().optional(),
  text: z.string(),
  opensLandingPanel: z.boolean().optional(),
  href: z.string().url().optional(),
  value: z.string().optional(),
});

const QuickInputOptionSchema = z.object({
  label: z.string(),
  value: z.string(),
  auiIcon: z.string().optional(),
  href: z.string().url().optional(),
});

const LandingPanelTitleSchema = z.union([z.string(), TextPropsSchema]);

const SupportWindowHeaderSchema = z.union([
  z.object({
    alt: z.never().optional(),
    src: z.never().optional(),
    text: z.string(),
  }),
  z.object({
    alt: z.string().optional(),
    src: z.string(),
    text: z.never().optional(),
  }),
]);

const DisplayModesSchema = z.enum(['FAB_TO_FAC', 'FAB', 'FAC', 'WINDOW', 'HIDDEN']);
const DisplayMode = DisplayModesSchema.enum;
export type DisplayModes = z.infer<typeof DisplayModesSchema>;
export const ShowActionDisplayModeSchema = DisplayModesSchema.exclude(['HIDDEN']);
export type ShowActionDisplayModes = z.infer<typeof ShowActionDisplayModeSchema>;

const FloatingActionCardBaseSchema = z.object({
  auiToken: z.enum(['asurion-light', 'asurion-dark', 'partner-light', 'partner-dark']),
  callbackConfirmationImg: z
    .object({
      alt: z.string().optional(),
      src: z.string(),
    })
    .optional(),
  chatLogo: z
    .object({
      alt: z.string().optional(),
      src: z.string(),
    })
    .optional(),
  cardLogo: z
    .object({
      alt: z.string().optional(),
      src: z.string(),
      fillColor: z.string().optional(),
    })
    .optional(),
  initialDisplayMode: DisplayModesSchema.optional(),
  chatPanelTextInputLabel: z.string().optional(),
  defaultOpened: z
    .boolean()
    .optional()
    .describe('@deprecated - This property will be removed in a future version. Use initialDisplayMode instead.'),
  experimental: z
    .object({
      mlContextualDialog: z
        .object({
          enabled: z.boolean().optional(),
          message: z.string().optional(),
          options: z.array(QuickInputOptionSchema).optional(),
        })
        .optional(),
    })
    .optional(),
  intents: z.array(QuickInputOptionSchema).optional(),
  landingPanelTextInputLabel: z.string().optional(),
  landingPanelShowInput: z.boolean().optional(),
  landingPanelTitle: LandingPanelTitleSchema.optional(),
  facTitle: z.string().optional(),
  fabNotificationIndicatorColor: z.string().optional(),
  logo: z.object({
    alt: z.string().optional(),
    src: z.string(),
    lottieUrl: z.string().optional(),
  }),
  position: z
    .object({
      desktop: z
        .object({
          offsetBottomPx: z.number().min(1).optional(),
          offsetRightPx: z.number().min(1).optional(),
        })
        .optional(),
      mobile: z
        .object({
          offsetBottomPx: z.number().min(1).optional(),
          offsetRightPx: z.number().min(1).optional(),
        })
        .optional(),
    })
    .optional(),
  supportWindowHeader: SupportWindowHeaderSchema.optional(),
  topics: z.array(TopicSchema).optional(),
  size: z.enum(['small', 'medium', 'large']).optional(),
  fullScreenMode: z.boolean().optional(),
});

const FloatingActionCardSchema = FloatingActionCardBaseSchema.merge(AuiTokenSchema).merge(
  TriggerOptionSchema.partial()
);

export const OptionsSchema = BaseOptionsSchema.extend({
  'floating-action-card': TriggerUnionSchema.and(AuiTokenSchema).and(
    FloatingActionCardSchema.transform((data) => {
      if (data.defaultOpened === true && data.initialDisplayMode === undefined) {
        data.initialDisplayMode = DisplayMode.WINDOW;
      }

      return data;
    })
  ),
});

export const PartialOptionsSchema = BaseOptionsSchema.extend({
  'floating-action-card': TriggerUnionSchema.and(FloatingActionCardSchema.partial()),
}).partial();

export type Topic = z.infer<typeof TopicSchema>;
export type Options = z.infer<typeof OptionsSchema>;
export type PartialOptions = z.infer<typeof PartialOptionsSchema>;
